import { Fragment } from 'react';
import { Label, Slider as AriaSlider, SliderThumb, SliderTrack } from 'react-aria-components';

import styled from 'styled-components';

import { Badge } from '@components/badge';
import { useControllableValue } from '@components/component-helpers';
import { InfoTooltip } from '@components/info-tooltip';
import { Stack } from '@components/stack';
import { VisuallyHidden } from '@components/visually-hidden';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

const getPaddedValue = (val: number, step: number) => {
  const decimalPlaces = (step.toString().split('.')[1] || '').length;
  return val.toFixed(decimalPlaces);
};

type SliderLabelProps = Pick<BaseProps, 'label' | 'hideLabel' | 'info'>;

const SliderLabel = ({ hideLabel, label, info }: SliderLabelProps) => {
  const LabelContainer = hideLabel ? VisuallyHidden : Fragment;
  return (
    <LabelContainer>
      <Stack orientation="horizontal" space={1} align="center">
        <Label data-slot="label">{label}</Label>
        {info && <InfoTooltip tooltip={info} />}
      </Stack>
    </LabelContainer>
  );
};

type BaseProps = {
  hideLabel?: boolean;
  hideDisplayValue?: boolean;
  label: string;
  minValue?: number;
  maxValue?: number;
  step?: number;
  isDisabled?: boolean;
  info?: string;
  formatDisplayValue?(value: number): string;
};

export type SliderProps = BaseProps & {
  defaultValue?: number;
  value?: number;
  onChange(newValue: number): void;
};

export const Slider = ({
  onChange,
  value: userValue,
  defaultValue = 0,
  minValue = 0,
  maxValue = 100,
  step = 1,
  isDisabled = false,
  hideDisplayValue = false,
  formatDisplayValue,
  ...rest
}: SliderProps) => {
  const [value, setValue] = useControllableValue(defaultValue, onChange, userValue);

  const displayValue = formatDisplayValue?.(value) ?? getPaddedValue(value, step);

  return (
    <StyledSlider
      isDisabled={isDisabled}
      value={value}
      onChange={(val) => {
        setValue(val as number);
      }}
      minValue={minValue}
      maxValue={maxValue}
      step={step}
    >
      <SliderLabel {...rest} />
      <div data-slot="track-container">
        <div data-slot="track-align" />
        <SliderTrack data-slot="slider-track">
          {({ state }) => (
            <>
              <div data-slot="track" />
              <div
                data-slot="fill"
                style={{
                  width: `calc(${state.getThumbPercent(0) * 100 + '%'} + ${pxToRem(16)})`,
                  marginLeft: pxToRem(-8),
                }}
              />
              <SliderThumb data-slot="slider-thumb" />
            </>
          )}
        </SliderTrack>
        {!hideDisplayValue && <Badge variant="neutral" size="md" text={displayValue} />}
      </div>
    </StyledSlider>
  );
};

export type MultiSliderProps = BaseProps & {
  defaultValue?: [number, number];
  value?: [number, number];
  onChange(newValue: [number, number]): void;
};

export const MultiSlider = ({
  value: userValue,
  onChange,
  defaultValue = [0, 10],
  minValue = 0,
  maxValue = 100,
  step = 1,
  isDisabled = false,
  hideDisplayValue = false,
  formatDisplayValue,
  ...rest
}: MultiSliderProps) => {
  const [value, setValue] = useControllableValue(defaultValue, onChange, userValue);
  const low = formatDisplayValue?.(value[0]) ?? getPaddedValue(value[0], step);
  const high = formatDisplayValue?.(value[1]) ?? getPaddedValue(value[1], step);

  return (
    <StyledSlider
      isDisabled={isDisabled}
      value={value}
      onChange={(newValue) => {
        setValue(newValue as [number, number]);
      }}
      minValue={minValue}
      maxValue={maxValue}
      step={step}
    >
      <SliderLabel {...rest} />
      <div data-slot="track-container">
        {!hideDisplayValue && <Badge variant="neutral" size="md" text={low} />}
        <SliderTrack data-slot="slider-track">
          {({ state }) => (
            <>
              <div data-slot="track" />
              <div
                data-slot="fill"
                style={{
                  left: state.getThumbPercent(0) * 100 + '%',
                  right: `calc(100% - ${state.getThumbPercent(1) * 100 + '%'})`,
                }}
              />
              <SliderThumb index={0} data-slot="slider-thumb" />
              <SliderThumb index={1} data-slot="slider-thumb" />
            </>
          )}
        </SliderTrack>
        {!hideDisplayValue && <Badge variant="neutral" size="md" text={high} />}
      </div>
    </StyledSlider>
  );
};

const StyledSlider = styled(AriaSlider)({
  display: 'flex',
  flexDirection: 'column',

  '&[data-disabled="true"] [data-slot="track-container"]': {
    opacity: 0.5,
  },

  '[data-slot="track-align"]': {
    marginLeft: pxToRem(-8),
  },

  '[data-slot="track-container"]': {
    display: 'flex',
    alignItems: 'center',
    gap: pxToRem(16),
    height: theme.base.height,
  },

  '[data-slot="slider-track"]': {
    position: 'relative',
    width: '100%',
    height: pxToRem(24),
    flexGrow: 1,
  },

  '[data-slot="label"]': {
    font: theme.font.semibold14,
    color: theme.form.label.color,
    lineHeight: pxToRem(24),
  },

  '[data-slot="track"]': {
    position: 'absolute',
    height: pxToRem(8),
    top: '50%',
    transform: 'translateY(-50%)',
    borderRadius: 9999,
    backgroundColor: theme.color.uiTrans10,
    width: `calc(100% + ${pxToRem(16)})`,
    marginLeft: pxToRem(-8),
  },

  '[data-slot="fill"]': {
    position: 'absolute',
    height: pxToRem(8),
    top: '50%',
    transform: 'translateY(-50%)',
    borderRadius: 9999,
    backgroundColor: theme.color.blue100,
  },

  '[data-slot="slider-thumb"]': {
    height: pxToRem(24),
    width: pxToRem(24),
    top: '50%',
    borderRadius: 9999,
    border: `${pxToRem(1)} solid ${theme.color.border20}`,
    backgroundColor: theme.color.white100,
    boxShadow: theme.shadow.low,
    transition: 'height 150ms ease-out, width 150ms ease-out',
    '&[data-focus-visible="true"]': {
      boxShadow: theme.shadow.focus,
      '--shadow-focus-color': theme.color.elementBlue100,
    },
    '&[data-dragging="true"], &[data-focus-visible="true"]': {
      height: pxToRem(26),
      width: pxToRem(26),
    },
  },
});
